/*
 * One fixed design canvas for every laptop and desktop.
 *
 * At a window 1024 px wide or more, the whole app lays out at CANVAS_WIDTH
 * (1440) CSS px and `zoom` on #root scales it to the window, like a slide: a
 * 1280 px laptop and a 2560 px monitor show the same composition. Below 1024 px
 * (tablets in portrait, phones) zoom stays 1 and the responsive stacked layout
 * applies, unscaled. The CSS media queries use the same 1023/1024 split, so at
 * the canvas width the desktop layout always applies.
 *
 * Under `zoom`, Chrome multiplies viewport units by the zoom too, so pages use
 * `var(--vw)`, `var(--vh)` and `var(--vmin)` (one hundredth of the viewport in
 * canvas px) instead of `vw`, `vh` and `vmin`. Outside the canvas they fall back
 * to the real units (tokens.css). `--app-zoom` is exposed on <html> for rules
 * that live outside #root, such as `scroll-padding-bottom`.
 *
 * The assistant panel: the Trapezium integration sets `--assistant-offset`
 * (real px) on :root. The canvas then fits the width beside the panel, and
 * #root gets the offset converted to canvas px so the frame, step bar and
 * drawer stay clear of the same strip.
 */
export const CANVAS_WIDTH = 1440;
export const CANVAS_MIN_VIEWPORT = 1024;

/* Zoom for a window `viewportWidth` px wide with an `assistantOffset` px panel open. */
export function computeZoom(viewportWidth, assistantOffset = 0) {
  if (!(viewportWidth >= CANVAS_MIN_VIEWPORT)) return 1;
  const available = Math.max(viewportWidth - (assistantOffset || 0), 320);
  return available / CANVAS_WIDTH;
}

function readAssistantOffset(html) {
  const raw = window.getComputedStyle(html).getPropertyValue('--assistant-offset');
  const px = parseFloat(raw);
  return Number.isFinite(px) && px > 0 ? px : 0;
}

let lastKey = '';

export function applyCanvasZoom() {
  const html = document.documentElement;
  const root = document.getElementById('root');
  if (!root) return;
  const width = window.innerWidth;
  const height = window.innerHeight;
  const offset = readAssistantOffset(html);
  const key = `${width}x${height}:${offset}`;
  if (key === lastKey) return;
  lastKey = key;

  const zoom = computeZoom(width, offset);
  const scaled = zoom !== 1;
  const round = (n) => `${Math.round(n * 1000) / 1000}px`;

  root.style.zoom = scaled ? String(zoom) : '';
  html.style.setProperty('--app-zoom', String(zoom));
  html.dataset.canvas = scaled ? 'fixed' : 'fluid';
  if (scaled) {
    const vw = width / zoom / 100;
    const vh = height / zoom / 100;
    html.style.setProperty('--vw', round(vw));
    html.style.setProperty('--vh', round(vh));
    html.style.setProperty('--vmin', round(Math.min(vw, vh)));
  } else {
    ['--vw', '--vh', '--vmin'].forEach((name) => html.style.removeProperty(name));
  }
  if (scaled && offset) root.style.setProperty('--assistant-offset', round(offset / zoom));
  else root.style.removeProperty('--assistant-offset');
}

/* Applies the zoom now and keeps it in step with the window and the assistant panel. */
export function startCanvasZoom() {
  applyCanvasZoom();
  window.addEventListener('resize', applyCanvasZoom);
  if (typeof MutationObserver === 'function') {
    // The integration toggles --assistant-offset on :root (inline style or a class).
    new MutationObserver(applyCanvasZoom).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
  }
}
